import { BudgetItem } from '@/hooks/use-annual-budget';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, PiggyBank, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProvisionDashboardProps {
  items: BudgetItem[];
  currentMonth: number;
}

export function ProvisionDashboard({ items, currentMonth }: ProvisionDashboardProps) {
  const navigate = useNavigate();
  const provisionItems = items.filter(i => i.provision_enabled);

  if (provisionItems.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <PiggyBank className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum item com provisão habilitada</p>
        <p className="text-sm mt-2">
          Habilite provisões nos itens sazonais para poupar mensalmente
        </p>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {provisionItems.map(item => {
        const totalNeeded = item.monthly_amount;
        const monthlyProvision = totalNeeded / item.provision_months;
        const expectedSaved = monthlyProvision * currentMonth;
        const targetMonth = item.active_months[0] || 1;
        const monthsUntilDue = targetMonth > currentMonth 
          ? targetMonth - currentMonth 
          : 12 - currentMonth + targetMonth;
        
        // Use real Goal data if linked, otherwise show as not linked
        const hasLinkedGoal = !!item.goal;
        const actualSaved = item.goal?.current_amount || 0;
        const progress = totalNeeded > 0 ? (actualSaved / totalNeeded) * 100 : 0;
        const isOnTrack = actualSaved >= expectedSaved * 0.9;
        const isComplete = actualSaved >= totalNeeded;

        return (
          <div key={item.id} className="bg-card border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium">{item.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {item.category?.name || 'Sem categoria'}
                </p>
              </div>
              {isComplete ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : isOnTrack ? (
                <PiggyBank className="h-5 w-5 text-blue-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
            </div>

            {hasLinkedGoal ? (
              <>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-1">
                      <PiggyBank className="h-3 w-3" />
                      {item.goal?.name}
                    </span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={Math.min(progress, 100)} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatCurrency(actualSaved)}</span>
                    <span>{formatCurrency(totalNeeded)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Poupar/mês</p>
                    <p className="font-medium">{formatCurrency(monthlyProvision)}</p>
                  </div>
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Faltam</p>
                    <p className="font-medium">{monthsUntilDue} meses</p>
                  </div>
                </div>

                {!isOnTrack && !isComplete && (
                  <div className="text-xs text-amber-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Faltam {formatCurrency(expectedSaved - actualSaved)} para estar em dia
                  </div>
                )}

                {isComplete && (
                  <div className="text-xs text-emerald-500 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Meta atingida! Pronto para o gasto.
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Vincule a um Cofrinho para acompanhar o saldo
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-muted-foreground text-xs">Meta</p>
                      <p className="font-medium">{formatCurrency(totalNeeded)}</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-muted-foreground text-xs">Poupar/mês</p>
                      <p className="font-medium">{formatCurrency(monthlyProvision)}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/goals')}
                    className="w-full"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Criar Cofrinho
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
